import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button, ErrorNotice, Field } from '@hive/ui';
import { api, errorMessage, type EntitlementDetails, type RightsCatalogue, type ScreenSetup, type VenueSetup } from '../api/client';
import { Dialog } from './Dialog';

/** yyyy-mm-dd for a date input, in UTC. */
export const dayOf = (iso?: string) => (iso ? iso.slice(0, 10) : '');
const startOf = (day: string) => new Date(`${day}T00:00:00Z`).toISOString();
const endOf = (day: string) => new Date(`${day}T23:59:59Z`).toISOString();
const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => new Date(Date.now() + n * 86400_000).toISOString().slice(0, 10);

export function ScreenDialog({ venueId, screen, onClose, onSaved }: { venueId: string; screen?: ScreenSetup; onClose(): void; onSaved(s: VenueSetup): void }) {
  const [name, setName] = useState(screen?.name ?? '');
  const [code, setCode] = useState(screen?.code ?? '');
  const [size, setSize] = useState(screen?.sizeInches ? String(screen.sizeInches) : '');
  const [connection, setConnection] = useState<'' | 'Ethernet' | 'Wi-Fi'>(screen?.connection ?? '');
  const [areaId, setAreaId] = useState(screen?.area?.id ?? '');
  const [areas, setAreas] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    void api.GET('/v1/venues/{venueId}/areas', { params: { path: { venueId } } }).then(({ data }) => data && setAreas(data));
  }, [venueId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const inches = size.trim() ? Number(size) : undefined;
    if (inches !== undefined && (!Number.isInteger(inches) || inches < 10 || inches > 400)) return setError('Screen size must be a whole number of inches, 10 to 400.');
    setBusy(true);
    setError(null);
    const body = { name: name.trim(), code: code.trim() || undefined, sizeInches: inches, connection: connection || undefined, areaId: areaId || undefined };
    const { data, error: err } = screen
      ? await api.PATCH('/v1/setup/venues/{venueId}/screens/{screenId}', { params: { path: { venueId, screenId: screen.id } }, body: { ...body, code: code.trim(), areaId } })
      : await api.POST('/v1/setup/venues/{venueId}/screens', { params: { path: { venueId } }, body });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    onSaved(data);
    onClose();
  }

  return (
    <Dialog
      titleId="screen-title"
      title={screen ? `Edit ${screen.name}` : 'Add a screen'}
      subtitle={screen ? undefined : 'Pair a player to it from Keeper once it is added.'}
      onClose={onClose}
      width={480}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="screen-form" disabled={busy}>
            {screen ? 'Save' : 'Add screen'}
          </Button>
        </>
      }
    >
      <form id="screen-form" className="mc-form" onSubmit={submit}>
        {error && <ErrorNotice title={error} />}
        <Field id="s-name" label="Screen name" placeholder="Main bar" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />
        <div className="mc-grid2">
          <Field id="s-code" label="Screen code (optional)" placeholder="HV-0420" value={code} onChange={(e) => setCode(e.target.value)} maxLength={40} />
          <Field id="s-size" label="Size in inches (optional)" inputMode="numeric" value={size} onChange={(e) => setSize(e.target.value)} />
        </div>
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="s-area">
            Area
          </label>
          <select id="s-area" className="hv-input" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
            <option value="">No area</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <span className="mc-tiny hv-muted">The venue manages its areas in Keeper (Screens and areas).</span>
        </div>
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="s-conn">
            Connection
          </label>
          <select id="s-conn" className="hv-input" value={connection} onChange={(e) => setConnection(e.target.value as typeof connection)}>
            <option value="">Not recorded</option>
            <option value="Ethernet">Ethernet</option>
            <option value="Wi-Fi">Wi-Fi</option>
          </select>
        </div>
      </form>
    </Dialog>
  );
}

export function LicenceDialog({ venueId, licence, onClose, onSaved }: { venueId: string; licence?: VenueSetup['licence']; onClose(): void; onSaved(s: VenueSetup): void }) {
  const [licenceNo, setLicenceNo] = useState(licence?.licenceNo ?? '');
  const [tier, setTier] = useState(licence?.tier ?? '');
  const [from, setFrom] = useState(dayOf(licence?.validFrom) || today());
  const [to, setTo] = useState(dayOf(licence?.validTo) || inDays(365));
  const [billing, setBilling] = useState(licence?.billing ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: err } = await api.PUT('/v1/setup/venues/{venueId}/licence', {
      params: { path: { venueId } },
      body: { licenceNo: licenceNo.trim(), tier: tier.trim(), validFrom: startOf(from), validTo: endOf(to), ...(billing.trim() ? { billing: billing.trim() } : {}) },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    onSaved(data);
    onClose();
  }

  return (
    <Dialog
      titleId="licence-title"
      title="Exhibition licence"
      subtitle="A new licence number is kept as a new record; the latest one counts."
      onClose={onClose}
      width={520}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="licence-form" disabled={busy}>
            Save licence
          </Button>
        </>
      }
    >
      <form id="licence-form" className="mc-form" onSubmit={submit}>
        {error && <ErrorNotice title={error} />}
        <div className="mc-grid2">
          <Field id="l-no" label="Licence number" placeholder="VL-2026-0143" value={licenceNo} onChange={(e) => setLicenceNo(e.target.value)} required maxLength={60} />
          <Field id="l-tier" label="Tier" placeholder="Hospitality B · up to 200" value={tier} onChange={(e) => setTier(e.target.value)} required maxLength={120} />
          <Field id="l-from" label="Valid from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          <Field id="l-to" label="Valid to" type="date" value={to} onChange={(e) => setTo(e.target.value)} required />
        </div>
        <Field id="l-billing" label="Billing (optional)" placeholder="Monthly · paid to 30 Sep" value={billing} onChange={(e) => setBilling(e.target.value)} maxLength={120} />
      </form>
    </Dialog>
  );
}

const LEVELS: { level: EntitlementDetails['level']; label: string; hint: string }[] = [
  { level: 'competition', label: 'Competition', hint: 'Every fixture in it' },
  { level: 'fixture', label: 'Single fixture', hint: 'One match or event' },
  { level: 'channel', label: 'Channel', hint: 'Everything on a feed' },
];

export function RightsDialog({
  venueId,
  catalogue,
  right,
  onClose,
  onSaved,
}: {
  venueId: string;
  catalogue: RightsCatalogue;
  right?: EntitlementDetails;
  onClose(): void;
  onSaved(s: VenueSetup): void;
}) {
  const [level, setLevel] = useState<EntitlementDetails['level']>(right?.level ?? 'competition');
  const [targetId, setTargetId] = useState(right?.targetId ?? '');
  const [from, setFrom] = useState(dayOf(right?.validFrom) || today());
  const [to, setTo] = useState(dayOf(right?.validTo) || inDays(180));
  const [territory, setTerritory] = useState(right?.territory ?? '');
  const [scope, setScope] = useState(right?.scope ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(() => {
    if (level === 'competition') return catalogue.competitions.map((c) => ({ id: c.id, label: `${c.name} (${c.shortName})` }));
    if (level === 'channel') return catalogue.channels.map((c) => ({ id: c.id, label: c.code ? `${c.name} · ${c.code}` : c.name }));
    return catalogue.fixtures.map((f) => ({
      id: f.id,
      label: [f.title, f.competition, f.startsAt && new Date(f.startsAt).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })]
        .filter(Boolean)
        .join(' · '),
    }));
  }, [level, catalogue]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!right && !targetId) return setError(`Choose a ${level === 'fixture' ? 'fixture' : level}.`);
    setBusy(true);
    setError(null);
    const dates = { validFrom: startOf(from), validTo: endOf(to), territory: territory.trim(), scope: scope.trim() };
    const { data, error: err } = right
      ? await api.PATCH('/v1/setup/venues/{venueId}/entitlements/{entitlementId}', { params: { path: { venueId, entitlementId: right.id } }, body: dates })
      : await api.POST('/v1/setup/venues/{venueId}/entitlements', {
          params: { path: { venueId } },
          body: { level, targetId, validFrom: dates.validFrom, validTo: dates.validTo, ...(dates.territory ? { territory: dates.territory } : {}), ...(dates.scope ? { scope: dates.scope } : {}) },
        });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    onSaved(data);
    onClose();
  }

  return (
    <Dialog
      titleId="rights-title"
      title={right ? `Rights to ${right.label}` : 'Grant rights'}
      subtitle={right ? 'Change the dates, territory or scope.' : 'What this venue may show, and when.'}
      onClose={onClose}
      width={600}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="rights-form" disabled={busy}>
            {right ? 'Save' : 'Grant rights'}
          </Button>
        </>
      }
    >
      <form id="rights-form" className="mc-form" onSubmit={submit}>
        {error && <ErrorNotice title={error} />}
        {!right && (
          <>
            <fieldset className="mc-fieldset">
              <legend className="hv-field__label">Covers</legend>
              <div className="mc-roles">
                {LEVELS.map((l) => (
                  <label key={l.level} className={`mc-role${level === l.level ? ' mc-role--on' : ''}`}>
                    <input type="radio" name="level" checked={level === l.level} onChange={() => (setLevel(l.level), setTargetId(''))} />
                    <span className="mc-strong">{l.label}</span>
                    <span className="mc-small hv-muted">{l.hint}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="hv-field">
              <label className="hv-field__label" htmlFor="r-target">
                {level === 'competition' ? 'Competition' : level === 'channel' ? 'Channel' : 'Fixture (last day to two weeks ahead)'}
              </label>
              <select id="r-target" className="hv-input" value={targetId} onChange={(e) => setTargetId(e.target.value)} required>
                <option value="">Choose…</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="mc-grid2">
          <Field id="r-from" label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          <Field id="r-to" label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} required />
          <Field id="r-territory" label="Territory (optional)" placeholder="UAE" value={territory} onChange={(e) => setTerritory(e.target.value)} maxLength={60} />
          <Field id="r-scope" label="Scope (optional)" placeholder="Live and delayed" value={scope} onChange={(e) => setScope(e.target.value)} maxLength={120} />
        </div>
      </form>
    </Dialog>
  );
}
